import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Shield, Users, ScrollText, KeyRound, LogOut } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import ConfirmDialog from "../../components/ConfirmDialog";

const NAV = [
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/audit-log", label: "Audit log", icon: ScrollText },
  { to: "/admin/change-password", label: "Change password", icon: KeyRound },
];

export default function AdminLayout() {
  const { admin, signOut } = useAdminAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const onConfirmSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate("/signin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Admin topbar — deliberately distinct from the user app to make the
          mode obvious. */}
      <header className="sticky top-0 z-30 border-b border-indigo-200 bg-indigo-900 text-white shadow">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-3 sm:px-6">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 flex-none text-amber-300" />
            <span className="text-sm font-semibold uppercase tracking-wide">
              Paysafi Admin
            </span>
          </div>
          <nav className="hidden items-center gap-1 sm:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-indigo-800 text-white"
                      : "text-indigo-200 hover:bg-indigo-800 hover:text-white"
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden truncate text-xs text-indigo-200 md:inline md:max-w-[200px]">
              {admin?.email}
            </span>
            <button
              onClick={() => setConfirmOpen(true)}
              className="flex items-center gap-1.5 rounded-md bg-indigo-800 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 sm:px-3"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        {/* Mobile nav */}
        <nav className="flex items-center gap-1 overflow-x-auto border-t border-indigo-800 px-3 py-2 sm:hidden">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-indigo-800 text-white"
                    : "text-indigo-200 hover:bg-indigo-800 hover:text-white"
                }`
              }
            >
              <item.icon className="h-3.5 w-3.5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <Outlet />
      </main>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => !signingOut && setConfirmOpen(false)}
        onConfirm={onConfirmSignOut}
        title="Sign out of admin console?"
        description="You'll need to enter your password and a new 2FA code to sign back in."
        confirmLabel={signingOut ? "Signing out…" : "Sign out"}
        loading={signingOut}
      />
    </div>
  );
}
